import { useEffect, useState } from 'react';
import chroma from 'chroma-js';

// find the nearest non-transparent background, so a block with no background of its own
// reads as whatever color it visually sits on, not black/blank
export function readComputedBackground(el: Element): string | undefined {
  let node: Element | null = el;
  while (node) {
    const bg = getComputedStyle(node).backgroundColor;
    if (chroma.valid(bg) && chroma(bg).alpha() > 0) return chroma(bg).hex();
    node = node.parentElement;
  }
  return undefined;
}

export function parsePx(value: string): number | undefined {
  const n = parseFloat(value);
  return Number.isNaN(n) ? undefined : n;
}

export interface ResolvedBlockStyle {
  background?: string;
  borderColor?: string;
  borderWidth?: number;
  hasBorder?: boolean;
  cornerTopLeft?: number;
  cornerTopRight?: number;
  cornerBottomRight?: number;
  cornerBottomLeft?: number;
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
}

// reads the block's live rendered style from the DOM (not otherwise exposed to this panel), so a
// fresh block shows real values instead of blank fields; margin is read off the outer wrapper td.
// It's read again whenever the block's own style changes, so clearing an override shows what the
// block falls back to rather than the override it had when the panel opened
export function useResolvedBlockStyle(blockId: string, style: object | undefined): ResolvedBlockStyle {
  const [resolved, setResolved] = useState<ResolvedBlockStyle>({});
  const styleKey = JSON.stringify(style ?? {});
  useEffect(() => {
    const el = document.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
    if (!el) return;
    const computed = getComputedStyle(el);
    // blocks whose padding lives on inner cells (e.g. the date & time card's rows) mark the cell to read it from
    const paddingComputed = getComputedStyle(el.querySelector('[data-block-padding]') ?? el);
    const outerTd = el.closest('table')?.parentElement;
    const outerComputed = outerTd ? getComputedStyle(outerTd) : undefined;
    setResolved({
      background: readComputedBackground(el),
      borderColor: chroma.valid(computed.borderColor) ? chroma(computed.borderColor).hex() : undefined,
      borderWidth: parsePx(computed.borderTopWidth),
      hasBorder: computed.borderTopStyle !== 'none',
      cornerTopLeft: parsePx(computed.borderTopLeftRadius),
      cornerTopRight: parsePx(computed.borderTopRightRadius),
      cornerBottomRight: parsePx(computed.borderBottomRightRadius),
      cornerBottomLeft: parsePx(computed.borderBottomLeftRadius),
      paddingTop: parsePx(paddingComputed.paddingTop),
      paddingRight: parsePx(paddingComputed.paddingRight),
      paddingBottom: parsePx(paddingComputed.paddingBottom),
      paddingLeft: parsePx(paddingComputed.paddingLeft),
      marginTop: outerComputed ? parsePx(outerComputed.paddingTop) : undefined,
      marginRight: outerComputed ? parsePx(outerComputed.paddingRight) : undefined,
      marginBottom: outerComputed ? parsePx(outerComputed.paddingBottom) : undefined,
      marginLeft: outerComputed ? parsePx(outerComputed.paddingLeft) : undefined,
    });
  }, [blockId, styleKey]);
  return resolved;
}
